import type { GenericFormType } from '@neo/blocks/forms/types'
import { find, isEqual, map, uniqueId } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { McpArticle, McpArticleCollection } from 'v2/pages/flow/knowledge-base/types'
import type { KBAppDocument, NewKBFormValues } from '../../../types'
import type { TreeViewItem } from '../document-tree-view'

// Utility type guards
function isMcpArticle(item: McpArticle | McpArticleCollection): item is McpArticle {
  return item.type === 'article'
}

function isMcpArticleCollection(
  item: McpArticle | McpArticleCollection,
): item is McpArticleCollection {
  return item.type === 'collection'
}

// Max page rows rendered under ONE space. The TreeView is not virtualized, so a space with thousands
// of pages (e.g. 2750) froze/crashed the page when expanded. Render this many + a "showing N of M"
// footer; the user selects the whole space to include the rest (selection carries the collection id).
// INVARIANT: keep this == the rag API's fetch cap (GET /kb/fetch-collection-articles `limit`, default
// 50). If the API ever returns MORE than this, the auto-check-children effect would check rows the
// tree can't render (leaving un-uncheckable ids in checkedIds), so the two caps must move together.
const PAGE_RENDER_CAP = 50

interface UseDocumentTreeProps {
  finalDocumentsData: (McpArticle | McpArticleCollection)[]
  defaultCheckedIds: string[]
  urlSelectionType: string
  selectedAppProvider: string
  form: GenericFormType<NewKBFormValues>
  isConnected: (provider: string) => boolean
  previousAppDataRef: React.MutableRefObject<KBAppDocument[]>
  isInitialLoadRef: React.MutableRefObject<boolean>
  setPreviewDocument: (doc: McpArticle | null) => void
  setPreviewDocumentId: (id: string) => void
  setIsPreviewOpen: (open: boolean) => void
  // Lazy picker: collection ids whose pages are being fetched on expand (drives the spinner row).
  loadingCollectionIds?: Set<string>
  // Lazy picker: collection id → the API capped its pages (more exist than returned) → show the
  // "showing first N — select the whole space" footer.
  collectionHasMore?: Record<string, boolean>
}

export function useDocumentTree({
  finalDocumentsData,
  defaultCheckedIds,
  urlSelectionType,
  selectedAppProvider,
  form,
  isConnected,
  previousAppDataRef,
  isInitialLoadRef,
  setPreviewDocument,
  setPreviewDocumentId,
  setIsPreviewOpen,
  loadingCollectionIds,
  collectionHasMore,
}: UseDocumentTreeProps) {
  const [checkedIds, setCheckedIds] = useState(new Set<string>())

  // Reset checked items when app provider changes or url_selection_type changes
  useEffect(() => {
    setCheckedIds(new Set<string>())
    previousAppDataRef.current = []
  }, [selectedAppProvider, urlSelectionType, previousAppDataRef])

  // Lazy picker: when a CHECKED collection's pages arrive (the user selected the whole space, then it
  // expanded), propagate the check to the newly-loaded children. Without this, getCheckState recomputes
  // the parent from its (now non-empty) children — none checked — and the space's checkbox visibly flips
  // OFF even though the whole space is still selected (app_data keeps the collection id either way).
  useEffect(() => {
    setCheckedIds((prev) => {
      if (prev.size === 0) return prev
      const next = new Set(prev)
      let changed = false
      const walk = (items: (McpArticle | McpArticleCollection)[]) => {
        for (const it of items) {
          if (isMcpArticleCollection(it)) {
            if (next.has(it.id)) {
              for (const child of it.items) {
                if (!next.has(child.id)) {
                  next.add(child.id)
                  changed = true
                }
              }
            }
            walk(it.items)
          }
        }
      }
      walk(Array.isArray(finalDocumentsData) ? finalDocumentsData : [])
      return changed ? next : prev
    })
  }, [finalDocumentsData])

  // Map the hierarchical data to TreeViewItem[]
  const treeData: TreeViewItem[] = useMemo(() => {
    if (!finalDocumentsData) return []

    const mapData = (items: (McpArticle | McpArticleCollection)[], depth = 0): TreeViewItem[] =>
      map(items, (itemData) => {
        if (isMcpArticleCollection(itemData)) {
          const mapped = mapData(itemData.items, depth + 1)
          // CAP rendered children: a space can have thousands of pages. The API caps the fetch (~50)
          // AND the non-virtualized TreeView would freeze if it ever got more — so render at most
          // PAGE_RENDER_CAP and, when the space has more (API has_more, or a non-capped source
          // overflowed), append a non-interactive footer telling the user to select the WHOLE space for
          // the rest (selection carries the collection id, so nothing is lost).
          const hasMore =
            (collectionHasMore?.[itemData.id] ?? false) || mapped.length > PAGE_RENDER_CAP
          const shown = mapped.slice(0, PAGE_RENDER_CAP)
          const children = hasMore
            ? [
                ...shown,
                {
                  id: `__more__${itemData.id}`,
                  name: `Showing first ${shown.length} pages — select the whole space to include all`,
                  type: 'info',
                  checked: false,
                  defaultChecked: false,
                  disableCheckbox: true,
                } as TreeViewItem,
              ]
            : mapped
          return {
            id: itemData.id,
            name: itemData.title || 'Untitled Collection',
            type: 'collection',
            children,
            checked: checkedIds.has(itemData.id),
            defaultChecked: defaultCheckedIds.includes(itemData.id),
            disableCheckbox: depth > 0,
            // Lazy picker: show a spinner row while this space's pages are being fetched on expand.
            isLoading: loadingCollectionIds?.has(itemData.id) ?? false,
          }
        }
        if (isMcpArticle(itemData)) {
          return {
            id: itemData.id,
            name: itemData.title || 'Untitled Page',
            type: 'article',
            checked: checkedIds.has(itemData.id),
            defaultChecked: defaultCheckedIds.includes(itemData.id),
            disableCheckbox: depth > 0,
          }
        }
        return {
          id: `unknown-${uniqueId()}`,
          name: 'Unknown',
          type: 'article',
          checked: false,
          defaultChecked: false,
          disableCheckbox: depth > 0,
        } // Ensure unique ID for fallbacks
      })
    return mapData(Array.isArray(finalDocumentsData) ? finalDocumentsData : [])
  }, [finalDocumentsData, checkedIds, defaultCheckedIds, loadingCollectionIds, collectionHasMore])

  // Icon map configuration (will be handled by component)
  const treeIconMapConfig = useMemo(() => ({ article: 'FileText' }), [])

  // Count only leaf nodes (articles) for accurate page count
  const countLeafNodes = useCallback(
    (items: TreeViewItem[]): number =>
      items.reduce((count, item) => {
        if (item.type === 'article') {
          return count + 1
        }
        if (item.children && item.children.length > 0) {
          return count + countLeafNodes(item.children)
        }
        return count
      }, 0),
    [],
  )

  const totalPageCount = useMemo(() => countLeafNodes(treeData), [treeData, countLeafNodes])

  // Header counts (the "78 pages available" badge was misleading — with lazy loading it counted only
  // the PAGES already loaded, while the tree LISTS all the spaces). Show the real top-level count + how
  // many things are selected instead.
  //   collectionCount: number of top-level spaces/collections (0 for a flat source like Notion).
  //   loosePageCount: top-level pages NOT under a collection (a source can mix both; the badge must not
  //     hide them behind the "N spaces" label).
  //   selectedCount: a whole checked space counts as 1; otherwise count individually-checked pages.
  const collectionCount = useMemo(
    () => treeData.filter((t) => t.type === 'collection').length,
    [treeData],
  )
  const loosePageCount = useMemo(
    () => treeData.filter((t) => t.type === 'article').length,
    [treeData],
  )
  const selectedCount = useMemo(() => {
    let n = 0
    const walk = (items: (McpArticle | McpArticleCollection)[]) => {
      for (const it of items) {
        if (checkedIds.has(it.id))
          n += 1 // whole space, or a single page
        else if (isMcpArticleCollection(it)) walk(it.items) // count its individually-checked pages
      }
    }
    walk(Array.isArray(finalDocumentsData) ? finalDocumentsData : [])
    return n
  }, [checkedIds, finalDocumentsData])

  // Preview logic for tree
  const handleTreeAction = useCallback(
    (_action: string, items: TreeViewItem[]) => {
      const fileItem = find(items, (item) => item.type === 'article')
      if (!fileItem) return
      // Find the McpArticle in the original data
      const findArticle = (
        collections: (McpArticle | McpArticleCollection)[],
      ): McpArticle | null => {
        let foundArticle: McpArticle | null = null
        collections.some((col) => {
          if (isMcpArticle(col) && col.id === fileItem.id) {
            foundArticle = col
            return true // Stop .some()
          }
          if (isMcpArticleCollection(col) && col.items) {
            const foundInSubCollection = findArticle(col.items) // Recursive call
            if (foundInSubCollection) {
              foundArticle = foundInSubCollection
              return true // Stop .some()
            }
          }
          return false // Continue .some()
        })
        return foundArticle
      }
      const article = findArticle(Array.isArray(finalDocumentsData) ? finalDocumentsData : [])
      if (article) {
        setPreviewDocument(article)
        setPreviewDocumentId(article.id)
        setIsPreviewOpen(true)
      }
    },
    [finalDocumentsData, setPreviewDocument, setPreviewDocumentId, setIsPreviewOpen],
  )

  // Menu items configuration (will be handled by component)
  const treeMenuItemsConfig = useMemo(
    () => [
      {
        id: 'preview',
        label: 'Preview',
        iconType: 'preview',
        action: (items: TreeViewItem[]) => handleTreeAction('preview', items),
      },
    ],
    [handleTreeAction],
  )

  const handleCheckChange = useCallback(
    (item: TreeViewItem, isChecked: boolean) => {
      setCheckedIds((prevCheckedIds) => {
        const newCheckedIds = new Set(prevCheckedIds)
        // Recursively update check state for an item and its children
        const updateItemAndChildren = (currentItem: TreeViewItem, check: boolean) => {
          // Skip ONLY the synthetic, non-selectable "Showing first N pages…" footer (a `__more__<id>`
          // row that must never enter checkedIds/app_data). Real child pages ALSO carry disableCheckbox
          // (depth > 0 — individually non-toggleable; the whole space is the selectable unit), but they
          // ARE part of their space's selection: the auto-check effect adds their ids when the space is
          // checked, so on UNCHECK we must recurse INTO them and delete those ids too. Skipping every
          // disableCheckbox item here (the old guard) left the auto-checked children in checkedIds, so
          // getCheckState kept deriving the space as checked/indeterminate — un-clearable, "Sync" stuck
          // disabled while the header said "N selected".
          if (currentItem.id.startsWith('__more__')) return
          if (check) {
            newCheckedIds.add(currentItem.id)
          } else {
            newCheckedIds.delete(currentItem.id)
          }
          if (currentItem.children) {
            currentItem.children.forEach((child) => updateItemAndChildren(child, check))
          }
        }
        updateItemAndChildren(item, isChecked)
        return newCheckedIds
      })
    },
    [setCheckedIds],
  )

  // Handle newly selected items (excluding default checked ones)
  const handleNewSelectionChange = useCallback(
    (newlySelectedItems: TreeViewItem[]) => {
      if (urlSelectionType === 'selected') {
        const appDataForForm: KBAppDocument[] = newlySelectedItems.map((item) => ({
          id: item.id,
          type: item.type as 'collection' | 'article',
        }))

        // Only update form if the data has actually changed
        const hasChanged = !isEqual(appDataForForm, previousAppDataRef.current)

        if (hasChanged) {
          previousAppDataRef.current = appDataForForm
          form.setFieldValue('app_data', appDataForForm)
        }
      }
    },
    [urlSelectionType, form, previousAppDataRef],
  )

  // Update form articles based on checked items or url_selection_type
  useEffect(() => {
    // If url_selection_type is 'all', set empty app_data (will sync all data)
    if (urlSelectionType === 'all' && selectedAppProvider && isConnected(selectedAppProvider)) {
      const appDataForForm: KBAppDocument[] = []
      const hasChanged = !isEqual(appDataForForm, previousAppDataRef.current)

      if (hasChanged) {
        previousAppDataRef.current = appDataForForm
        form.setFieldValue('app_data', appDataForForm)
      }
      return
    }

    // For 'selected' type, handle document selection as before
    if (urlSelectionType === 'selected') {
      if (!finalDocumentsData || !Array.isArray(finalDocumentsData)) return

      const appDataForForm: KBAppDocument[] = []

      const collectCheckedFiles = (items: (McpArticle | McpArticleCollection)[]) => {
        items.forEach((item) => {
          if (checkedIds.has(item.id)) {
            appDataForForm.push({
              id: item.id,
              type: item.type as 'collection' | 'article',
            })
          }
        })
      }

      collectCheckedFiles(finalDocumentsData)

      // Only update form if the data has actually changed
      const hasChanged = !isEqual(appDataForForm, previousAppDataRef.current)

      if (hasChanged) {
        previousAppDataRef.current = appDataForForm
        form.setFieldValue('app_data', appDataForForm)
      }
    }

    // Mark that initial load is complete
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false
    }
  }, [
    checkedIds,
    finalDocumentsData,
    urlSelectionType,
    selectedAppProvider,
    isConnected,
    form,
    previousAppDataRef,
    isInitialLoadRef,
  ])

  return {
    treeData,
    treeIconMapConfig,
    treeMenuItemsConfig,
    totalPageCount,
    collectionCount,
    loosePageCount,
    selectedCount,
    handleCheckChange,
    handleNewSelectionChange,
    handleTreeAction,
  }
}
